import { t } from "components/CentralDataMangement/translation/Translation";
import Title from "components/common/atoms/Title";
import OfficeTargetTable from "../common/OfficeTargetTable";
import { Candidate, CooAgreementDetails, OfficeTarget, ProspectCallDetails, VacancyDetails, VisitsDetails, WeeklyReportsData } from "../Annotations";
import { useEffect, useMemo, useState } from "react";
import { CircularProgressbarWithChildren } from "react-circular-progressbar";
import WeeklyReportTable from "../common/WeeklyReportTable";
import VacanciesTable from "../common/VacanciesTable";
import VisitsTable from "../common/VisitsTable";
import ProspectCallsTable from "../common/ProspectCallsTable";
import CooAgreementTable from "../common/CooAgreementTable";
import TableTitle from "components/common/atoms/TableTitle";
import Tick from "static/images/Tick";
import EncryptDecryptService from "components/common/services/EncryptDecryptService";
import CustomNotify from "components/common/atoms/CustomNotify";
import { ApiCall } from "components/common/services/ApiServices";
import { EDIT_WEEKLY_REPORTS } from "../../../../routes/ApiEndpoints";
import { CENTRAL_DATA_MANAGEMENT_MICROSERVICE } from "Constants";
import { Link, useSearchParams } from "react-router-dom";
import LoadingIcon from "components/common/utlis/LoadingIcon";
import AccessControl from "components/common/services/RolesAndPermissions/AccessControl";

interface TargetData {
  officeQTarget: OfficeTarget;
  officeTarget: OfficeTarget;
}

interface ReportsData {
  primaryPeriodId: number | string | null;
  weeklyReports: WeeklyReportsData;
  vacancies: VacancyDetails[];
  foreignVacancies: VacancyDetails[];
  visits: VisitsDetails[];
  cooAgreement: CooAgreementDetails[];
  prospectCalls: ProspectCallDetails[];
  from?: string | null;
  to?: string | null;
  amName?: string;
  /** Per-locale activity wording keyed by follow-up key, supplied by the report API. */
  activityLabels?: Record<string, string>;
}

const WeeklyReports: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [targetData, setTargetData] = useState<TargetData>();
  const [reports, setReports] = useState<ReportsData>({
    primaryPeriodId: null,
    weeklyReports: {},
    vacancies: [],
    foreignVacancies: [],
    visits: [],
    prospectCalls: [],
    cooAgreement: [],
  });
  const [candidates, setCandidates] = useState<Candidate[]>([]);

  // let data = secureLocalStorage.getItem('omWeeklyReport') as string; // Get the 'data' query param as a string
  const [searchParams] = useSearchParams();
  const data = searchParams.get("data");

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchData();
    }, 3000); // 3 seconds

    return () => clearTimeout(timeout); // Cleanup the timeout on component unmount
  }, []);

  const fetchData = async () => {
    let parsedData = {};
    try {
      if (data) {
        // Decrypt the recruiterData
        let decryptedData = decodeURIComponent(EncryptDecryptService.decryptData(data));

        // If decryptedData is in JSON format, parse it
        parsedData = JSON.parse(decryptedData);
      }

    } catch (error) {
      CustomNotify({
        type: "error",
        message: t("Something went wrong"),
      });
      return null;
    }

    const response = await ApiCall.service(
      EDIT_WEEKLY_REPORTS,
      "POST",
      { ...parsedData, type: "accountManager" },
      false,
      CENTRAL_DATA_MANAGEMENT_MICROSERVICE
    );

    if (response?.status === 200) {
      setTargetData(response?.data?.targets);
      setReports({
        primaryPeriodId: response?.data?.primaryPeriodId,
        weeklyReports: response?.data?.weeklyReports,
        vacancies: response?.data?.vacancies,
        foreignVacancies: response?.data?.foreignVacancies,
        visits: response?.data?.visits,
        prospectCalls: response?.data?.prospectCalls,
        cooAgreement: response?.data?.coopAgreements,
        from: response?.data?.from,
        to: response?.data?.to,
        amName: response?.data?.amName,
        // Per-locale activity wording from the API; the state object is built field by field,
        // so this has to be carried over explicitly or the rows fall back to the English keys.
        activityLabels: response?.data?.activityLabels
      });
      setCandidates(response?.data?.candidates);
    } else {
      CustomNotify({
        type: "error",
        message: t("Some error occured")
      });
    }
    setLoading(false);
  }
  const foreignOffice = targetData?.officeQTarget?.isForeign

  // const rows = useMemo(() => [
  //   { key: 'visits', label: 'Visits' },
  //   { key: 'workplace_visit', label: 'Workplace visits' },
  //   { key: 'vacancies', label: 'Vacancies' },
  //   { key: 'vacancies_fro', label: 'Vacancies FRO' },
  //   { key: 'prospect_calls', label: 'Prospect call' },
  //   { key: 'starter', label: 'Starter' },
  //   { key: 'stopper', label: 'Stopper' },
  //   { key: 'stopper_permanent', label: 'Stopper permanent' },
  // ], []);

  const rows = useMemo(() => {
  const baseRows = [
    { key: 'visits', label: 'Visits' },
    { key: 'workplace_visit', label: 'Workplace visits' },
    { key: 'vacancies', label: 'Vacancies' },
    { key: 'vacancies_fro', label: 'Vacancies FRO' },
    { key: 'prospect_calls', label: 'Prospect call' },
    { key: 'starter', label: 'Starter' },
    { key: 'stopper', label: 'Stopper' },
    { key: 'stopper_permanent', label: 'Stopper permanent' },
  ];

  return foreignOffice
    ? baseRows.filter(row => row.key !== 'vacancies')
    : baseRows;
}, [foreignOffice]);

  return (
    <AccessControl
      requiredPermissions={[
        {
          permission: "Reports",
          read: true,
        },
      ]}
      renderNoAccess={true}
    >
      <Title title={`${t("Reporting (weekly account manager)")} - ${targetData?.officeQTarget?.officeName ?? ""} (${reports?.from ?? "-"} to ${reports?.to ?? "-"})`} />
      {loading ? (
        <div style={{ height: "38vw" }} className="d-flex align-items-center justify-content-center">
          <LoadingIcon
            iconType="bars"
            color="#e55496"
            className="m-auto"
            height={"5vw"}
            width={"5vw"}
          />
        </div>

      ) : (
        <>
          <div className="row py-3">
            <div className="col-md-3 mb-3">
              <OfficeTargetTable
                data={targetData?.officeQTarget}
                tableTitle={`Office Q`}
              />
            </div>
            <div className="col-md-3 mb-3">
              <div className="position-relative">
                <div
                  className={`circular-progress-bar-custom recruitment-card-title `}
                  data-label={`€ ${targetData?.officeTarget.reached ?? 0}`}
                >
                  <div>
                    <CircularProgressbarWithChildren
                      value={targetData?.officeTarget?.percentage ?? 0}
                      className="CircularProgressbarWithChildren"
                    >
                      <div className="text-center CircularProgressbar-text text-uppercase">
                        {t("Goal")}
                        <br />
                        <div>{`€ ${targetData?.officeTarget.target ?? 0}`}</div>
                      </div>
                    </CircularProgressbarWithChildren>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-6 mb-3">
              <WeeklyReportTable data={reports.weeklyReports} rows={rows} tableTitle={reports?.amName} activityLabels={reports?.activityLabels}/>
            </div>
            {!foreignOffice &&
            <div className="col-md-6">
              <VacanciesTable data={reports.vacancies} tableTitle={t("Vacancies")} />
            </div>
            }
            <div className="col-md-6">
              <VacanciesTable data={reports.foreignVacancies} isFroTable={true} tableTitle={t("Vacancies FRO")} />
            </div>
            <div className="col-md-6">
              <VisitsTable data={reports.visits} tableTitle={t("Visits")} />
            </div>
            <div className="col-md-6">
              <CooAgreementTable
                data={reports.cooAgreement}
                tableTitle={t("Cooperation agreement")}
              />
            </div>
            <div className="col-md-6">
              <ProspectCallsTable
                data={reports.prospectCalls}
                tableTitle={t("Prospect call")}
              />
            </div>
          </div>
          <div className="row pb-5">
            <TableTitle title={t("Recruitment")} />
            <div className="marginBotttom1 weekly-report-table scrollBarDesign">
              <table className="table table-hover table-bordered targetTable">
                <thead>
                  <tr className="TableHeader">
                    <th className="text-break text-center" style={{ width: "15%" }}>
                      {t("Candidate")}
                    </th>
                    <th className="text-break text-center" style={{ width: "16%" }}>
                      {t("Profile")}
                    </th>
                    <th className="text-break text-center" style={{ width: "7%" }}>
                      {t("Registration")}
                    </th>
                    <th className="text-break text-center" style={{ width: "10%" }}>
                      {t("Registration FRO")}
                    </th>
                    <th className="text-break text-center" style={{ width: "8%" }}>
                      {t("Re-registration")}
                    </th>
                    <th className="text-break text-center" style={{ width: "11%" }}>
                      {t("Re-registration FRO")}
                    </th>
                    <th className="text-break text-center" style={{ width: "7%" }}>
                      {t("Presentation")}
                    </th>
                    <th className="text-break text-center" style={{ width: "10%" }}>
                      {t("Presentation FRO")}
                    </th>
                    <th className="text-break text-center" style={{ width: "6%" }}>
                      {t("Interview")}
                    </th>
                    <th className="text-break text-center" style={{ width: "5%" }}>
                      {t("Starter")}
                    </th>
                    <th className="text-break text-center" style={{ width: "5%" }}>
                      {t("Stopper")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {candidates && candidates.length > 0 ? (
                    <>
                      {candidates.map((candidate: Candidate, index) => (
                        <tr key={index}>
                          <td className="align-middle text-center">{candidate.candidate}</td>
                          <td className="align-middle text-center">{candidate.profile}</td>
                          <td className="table-action-icons align-middle text-center">
                            {candidate.registration > 0 ? <Tick /> : ""}
                          </td>
                          <td className="table-action-icons align-middle text-center">
                            {candidate.registration_fro > 0 ? <Tick /> : ""}
                          </td>
                          <td className="table-action-icons align-middle text-center">
                            {candidate.re_registration > 0 ? <Tick /> : ""}
                          </td>
                          <td className="table-action-icons align-middle text-center">
                            {candidate.re_registration_fro > 0 ? <Tick /> : ""}
                          </td>
                          <td className="text-center">
                            <div className="numberWrapper">
                              {candidate.presentation}
                            </div>
                          </td>
                          <td className="text-center">
                            <div className="numberWrapper">
                              {candidate.presentation_fro}
                            </div>
                          </td>
                          <td className="text-center">
                            <div className="numberWrapper">
                              {candidate.interview}
                            </div>
                          </td>
                          <td className="table-action-icons align-middle text-center">
                            {candidate.starter > 0 ? <Tick /> : ""}
                          </td>
                          <td className="table-action-icons align-middle text-center">
                            {candidate.stopper > 0 ? <Tick /> : ""}
                          </td>
                        </tr>
                      ))}
                    </>
                  ) : (
                    <tr className="border rounded-3">
                      <td colSpan={10}>
                        <div className="text-danger text-center">
                          {t("No candidates found")}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div className="row backPadding">
            <div className="col-md-4 align-self-center">
              <Link
                to="/manage-reports"
                className=" back-btn text-decoration-underline"
              >
                {t("Back")}
              </Link>
            </div>
          </div>
        </>
      )}
    </AccessControl>
  );
};

export default WeeklyReports;
